/**
 * Report Models
 *
 * Shared interfaces for FAA and State compliance reports.
 */

// ============================================
// REPORT STATUS
// ============================================

export type ReportStatus = 'draft' | 'generating' | 'ready' | 'submitted' | 'failed';

// ============================================
// FAA REPORT INTERFACES
// ============================================

export interface FaaReportOptions {
  includeFlightLogs: boolean;
  includeSprayRecords: boolean;
  includePilotInfo: boolean;
  includeAircraftInfo: boolean;
}

export interface FaaGeneratedReport {
  id: string;
  reportType: string;
  reportTypeValue: string;
  dateRange: string;
  startDate: string;
  endDate: string;
  generatedAt: string;
  status: ReportStatus;
  recordCount: number;
  fileSize?: string;
  pdfUrl?: string;
  pdfGeneratedAt?: string;
  submittedAt?: string;
  submittedTo?: string;
  generationProgress?: number;
  options: FaaReportOptions;
}

// ============================================
// STATE REPORT INTERFACES
// ============================================

export interface StateReportOptions {
  includeChemicals: boolean;
  includeApplicationRates: boolean;
  includeFieldMaps: boolean;
  includeWeatherData: boolean;
}

export interface StateGeneratedReport {
  id: string;
  reportType: string;
  reportTypeValue: string;
  state: string;
  stateCode: string;
  dateRange: string;
  startDate: string;
  endDate: string;
  generatedAt: string;
  status: ReportStatus;
  recordCount: number;
  fileSize?: string;
  pdfUrl?: string;
  pdfGeneratedAt?: string;
  submittedAt?: string;
  submittedTo?: string;
  generationProgress?: number;
  options: StateReportOptions;
}

// ============================================
// REPORT TYPE OPTIONS
// ============================================

export const FAA_REPORT_TYPES = [
  { value: 'all', label: 'Complete FAA Report' },
  { value: 'part107', label: 'Part 107 Compliance' },
  { value: 'part137', label: 'Part 137 Operations' },
  { value: 'waiver', label: 'Waiver Operations' },
  { value: 'incident', label: 'Incident Reports' }
] as const;

export const STATE_REPORT_TYPES = [
  { value: 'pesticide', label: 'Pesticide Application Report' },
  { value: 'restricted', label: 'Restricted Use Pesticide Report' },
  { value: 'drift', label: 'Drift Incident Report' },
  { value: 'environmental', label: 'Environmental Compliance' },
  { value: 'annual', label: 'Annual Summary Report' }
] as const;
